import React, { useState } from 'react';

export function ContactForm({ title = 'Send en forespørgsel', subtitle = 'Udfyld formularen, så vender vi hurtigt tilbage til dig.' }) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    besked: '',
    website_url: '', // Honeypot antispam field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) {
      errs.navn = 'Angiv venligst dit navn';
    }
    if (!formData.telefon.trim()) {
      errs.telefon = 'Angiv venligst dit telefonnummer';
    } else if (!/^[0-9+ \-()]{6,20}$/.test(formData.telefon.trim())) {
      errs.telefon = 'Angiv venligst et gyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      errs.email = 'Angiv venligst din emailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Angiv venligst en gyldig emailadresse';
    }
    if (!formData.besked.trim()) {
      errs.besked = 'Skriv venligst en kort beskrivelse af opgaven';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Check honeypot: if bot filled this, silently return
    if (formData.website_url) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          navn: formData.navn.trim(),
          telefon: formData.telefon.trim(),
          email: formData.email.trim(),
          besked: formData.besked.trim(),
          website_url: formData.website_url, // Required honeypot
        }),
      });

      if (!res.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring på 69 30 64 45.');
      }

      setStatus('success');
      setFormData({
        navn: '',
        telefon: '',
        email: '',
        besked: '',
        website_url: '',
      });
    } catch (err) {
      console.error('Submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Der skete en fejl. Ring gerne direkte på 69 30 64 45.');
    }
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-6 sm:p-8 md:p-10 shadow-sm">
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="text-sm text-slate-600 mt-1">
            {subtitle}
          </p>
        )}
      </div>

      {status === 'success' ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-6 text-center">
          <div className="text-emerald-600 text-3xl mx-auto mb-3 text-center">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-semibold text-emerald-900 mb-1">
            Mange tak for din henvendelse!
          </h4>
          <p className="text-sm text-emerald-800 leading-relaxed max-w-md mx-auto">
            Vi har modtaget din besked og kontakter dig hurtigst muligt. Har du en hastesag, er du altid velkommen til at ringe på{' '}
            <a href="tel:69306445" className="font-semibold underline whitespace-nowrap">
              69 30 64 45
            </a>.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-5 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-800 bg-white border border-emerald-300 rounded hover:bg-emerald-50 transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans per Rule 12 */}
          <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave empty)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status === 'error' && (
            <div className="p-3.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation mt-0.5 text-red-500" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <label htmlFor="navn" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                Navn <span className="text-sky-600">*</span>
              </label>
              <input
                type="text"
                id="navn"
                name="navn"
                value={formData.navn}
                onChange={handleChange}
                placeholder="F.eks. Anders Jensen"
                className={`w-full px-3.5 py-2.5 text-sm bg-slate-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-600 focus:bg-white transition-colors ${
                  errors.navn ? 'border-red-400 bg-red-50/20' : 'border-slate-300 text-slate-900'
                }`}
                required
              />
              {errors.navn && <p className="text-xs text-red-600 mt-1">{errors.navn}</p>}
            </div>

            <div>
              <label htmlFor="telefon" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                Telefonnummer <span className="text-sky-600">*</span>
              </label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                value={formData.telefon}
                onChange={handleChange}
                placeholder="F.eks. 69 30 64 45"
                className={`w-full px-3.5 py-2.5 text-sm bg-slate-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-600 focus:bg-white transition-colors ${
                  errors.telefon ? 'border-red-400 bg-red-50/20' : 'border-slate-300 text-slate-900'
                }`}
                required
              />
              {errors.telefon && <p className="text-xs text-red-600 mt-1">{errors.telefon}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
              Email <span className="text-sky-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="F.eks. anders@eksempel.dk"
              className={`w-full px-3.5 py-2.5 text-sm bg-slate-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-600 focus:bg-white transition-colors ${
                errors.email ? 'border-red-400 bg-red-50/20' : 'border-slate-300 text-slate-900'
              }`}
              required
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="besked" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
              Beskrivelse af opgaven <span className="text-sky-600">*</span>
            </label>
            <textarea
              id="besked"
              name="besked"
              rows={4}
              value={formData.besked}
              onChange={handleChange}
              placeholder="Beskriv hvad der er galt med maskinen (f.eks. mærke, model eller fejl)..."
              className={`w-full px-3.5 py-2.5 text-sm bg-slate-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-600 focus:bg-white transition-colors resize-y ${
                errors.besked ? 'border-red-400 bg-red-50/20' : 'border-slate-300 text-slate-900'
              }`}
              required
            />
            {errors.besked && <p className="text-xs text-red-600 mt-1">{errors.besked}</p>}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm tracking-wide rounded-lg transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
            >
              {status === 'submitting' ? (
                <span>Sender...</span>
              ) : (
                <span>Send forespørgsel</span>
              )}
            </button>
            <p className="text-[11px] text-slate-500 mt-2">
              Vi vender tilbage hurtigst muligt. Du bliver kontaktet 20 minutter før ankomst.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}

export default ContactForm;
